import { Classes, Dialog } from "@blueprintjs/core";
import * as React from "react";
import styled from "styled-components";
import {
  SiegeLieuSuiviFichierApi,
  SiegeLieuSuiviFichierUploadDto,
  SiegeLieuSuiviFichierUploadDtoFromJSON
} from "../../../../../../api";
import { useApiService, useCrudApi, useTl } from "../../../../../../hooks";
import * as Yup from "yup";
import { ETLCodes } from "../../../../../../locales";
import { useReferential } from "../../../../../../hooks/useReferential";
import { FGWalterFileInput, FGWalterSelectInput, SmallFormGenerator } from "../../../../../../components";
import { FieldGroup } from "nsitools-react";

const StyledDialog = styled(Dialog)`
  width: 600px;
  height: auto;
  background-color: white;
`;

export interface ISiegeLieuSuiviDocumentUploadDialogProps {
  idsuivi: number;
  dialogOpen: boolean;
  onClose: (refresh?: boolean) => void;
}

export const SiegeLieuSuiviDocumentUploadDialog: React.FunctionComponent<ISiegeLieuSuiviDocumentUploadDialogProps> = ({
  idsuivi,
  dialogOpen,
  onClose
}) => {
  const { t } = useTl();
  const api = useApiService(SiegeLieuSuiviFichierApi);

  const { data, loading, saveItem, saving, validationErrors } = useCrudApi(
    React.useMemo(
      () => ({
        getApiFn: () => {
          return SiegeLieuSuiviFichierUploadDtoFromJSON({ idsiegeSocialLieuFormationSuivi: idsuivi });
        },
        saveApiFn: async (d: SiegeLieuSuiviFichierUploadDto) => {
          return api.siegeLieuSuiviFichierUploadSuiviFichier({
            SiegeLieuSuiviFichierUploadDto: d
          });
        },
        onSaved: () => onClose(true)
      }),
      [api, idsuivi, onClose]
    )
  );

  const FormSchema = React.useMemo(
    () =>
      Yup.object().shape({
        idtypeSuiviFichier: Yup.number().nullable(),
        file: Yup.object()
          .nullable()
          .shape({
            fileName: Yup.string()
              .nullable()
              .required(t(ETLCodes.Required)),
            contentB64: Yup.string()
              .nullable()
              .required(t(ETLCodes.Required))
          })
      }),
    [t]
  );

  const [typesSuiviFichier, tLoading] = useReferential(a => a.referentialGetTypesSuiviFichier());

  return (
    <StyledDialog title={t(ETLCodes.UploadFichier)} isOpen={dialogOpen} onClose={() => onClose(false)}>
      <div className={Classes.DIALOG_BODY}>
        <SmallFormGenerator
          initialValues={data}
          onSubmit={saveItem}
          editMode={true}
          validationSchema={FormSchema}
          onCancel={() => onClose(false)}
          showDeleteButton={false}
          saving={saving}
          loading={loading}
          validationErrors={validationErrors}
        >
          <FieldGroup>
            <FGWalterFileInput name="file" label={t(ETLCodes.Fichier)} requiredMark />
            <FGWalterSelectInput
              name="idtypeSuiviFichier"
              label={t(ETLCodes.Type)}
              items={typesSuiviFichier}
              loading={tLoading}
            />
          </FieldGroup>
        </SmallFormGenerator>
      </div>
    </StyledDialog>
  );
};
